import { RateLimiter } from 'src/queries/pusherRateLimiter'

/**
 * Turns a Pusher entity event into a React Query cache update behind a shared
 * rate limiter + circuit breaker. Policy and flow: "Pusher → React Query sync"
 * in src/queries/AGENTS.md.
 */

export const PUSHER_ENTITY_ACTION = {
  created: 'CREATED',
  updated: 'UPDATED',
  deleted: 'DELETED',
} as const

export interface CircuitBreaker {
  isOpen: () => boolean
  trip: () => void
  reset: () => void
}

export interface CircuitBreakerOptions {
  /** How long to stay open after a trip. Default 30s. */
  cooldownMs?: number
  now?: () => number
}

export const createCircuitBreaker = ({
  cooldownMs = 30_000,
  now = Date.now,
}: CircuitBreakerOptions = {}): CircuitBreaker => {
  let openUntil = 0
  return {
    isOpen: () => now() < openUntil,
    trip: () => {
      openUntil = now() + cooldownMs
    },
    reset: () => {
      openUntil = 0
    },
  }
}

export interface EntityPusherSyncConfig<E> {
  /** Bounds upstream fetches per rolling window (default 60/min). */
  limiter: RateLimiter
  /** Opens after an upstream rate-limit response to stop a feedback storm. */
  breaker: CircuitBreaker
  /** Fetch one entity by id (the potentially rate-limited upstream read). */
  fetchDetail: (id: string) => Promise<E | undefined>
  /** Patch detail + list caches (and any Redux parity) from a fetched entity. */
  applyUpdate: (entity: E) => void
  /** Remove an entity from list caches (DELETED). No fetch. */
  removeFromLists: (id: string) => void
  /** A new entity appeared (CREATED). Usually a narrow list invalidation. */
  onCreate?: (id: string) => void
  /** Fetch deliberately skipped — mark stale without refetching now. */
  onOverflow: (
    id: string,
    reason: 'rate-limited' | 'breaker-open' | 'upstream-429',
  ) => void
  /** Detects an upstream rate-limit (HTTP 429) error. */
  isRateLimitError: (error: unknown) => boolean
  /** Optional hook for surfacing dropped/deferred events (telemetry/logging). */
  onDeferred?: (id: string, reason: string) => void
}

/**
 * Rate-limited optimistic refresh of one entity: fetch-and-patch when budget
 * allows, degrade to onOverflow otherwise. The standard replacement for a
 * legacy per-id refresh dispatch — see src/queries/AGENTS.md.
 */
export const refreshEntity = async <E>(
  id: string,
  config: EntityPusherSyncConfig<E>,
): Promise<void> => {
  if (config.breaker.isOpen()) {
    config.onDeferred?.(id, 'breaker-open')
    config.onOverflow(id, 'breaker-open')
    return
  }

  if (!config.limiter.tryAcquire()) {
    config.onDeferred?.(id, 'rate-limited')
    config.onOverflow(id, 'rate-limited')
    return
  }

  let entity: E | undefined
  try {
    entity = await config.fetchDetail(id)
  } catch (error) {
    if (config.isRateLimitError(error)) {
      config.breaker.trip()
      config.onDeferred?.(id, 'upstream-429')
      config.onOverflow(id, 'upstream-429')
    }
    // Non-rate-limit fetch errors are swallowed: parity with the legacy thunk.
    return
  }
  if (entity) config.applyUpdate(entity)
}

export const handleEntityPusherEvent = async <E>(
  event: { id: string; action: string },
  config: EntityPusherSyncConfig<E>,
): Promise<void> => {
  const { id, action } = event
  if (!id) return

  if (action === PUSHER_ENTITY_ACTION.deleted) {
    config.removeFromLists(id)
    return
  }

  if (action === PUSHER_ENTITY_ACTION.created) {
    config.onCreate?.(id)
    return
  }

  // UPDATED (and any other action that means "this entity changed").
  return refreshEntity(id, config)
}
